import { Location } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    inject,
    OnInit,
    output,
    signal,
    viewChild
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import {
    ApiRequestService,
    DateUtilService,
    LoadingOverlayComponent,
    MAT_ICON,
    Material,
    DatepickerComponent,
    SelectComponent,
    SelectOption,
    TOAST_STATE,
    ToastmessageService,
    TranslationPipe,
    TranslationService,
    Void
} from '@prekog/common-ui';
import { forkJoin, Observable, of, switchMap } from 'rxjs';
import { FUTURE_DATE_ERROR, SUCCESSFULLY_MODIFIED } from 'src/app/shared/adminconstants';
import { Festival } from 'src/app/shared/festival';
import { ReferenceDataService } from 'src/app/shared/reference-data.service';
import { StorageType } from 'src/app/shared/storagetype.enum';
import { Partner } from '../../shared/partner';
import { ProductEventType } from '../../shared/producteventtype.enum';
import { ProductEvent } from '../productevent';
import { FormStateConfig, PRODUCT_EVENT_FORM_RULES } from '../producteventform.config';
import { ProductEventFormHelperService } from '../producteventformhelper.service';
import { RecalculationService } from '../recalculation.service';
import { UpdateProductEventRequest } from './updateproductevent.request';

const SELECTABLE_STORAGE_TYPES: (keyof typeof StorageType)[] = ['OFFICE', 'MULTIVERZUM_WAREHOUSE'];

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-editproductevent',
    templateUrl: './editproductevent.component.html',
    styleUrls: ['./editproductevent.component.scss'],
    standalone: true,
    imports: [
        MatIconModule,
        FormsModule,
        TranslationPipe,
        ReactiveFormsModule,
        SelectComponent,
        DatepickerComponent,
        LoadingOverlayComponent
    ]
})
export class EditProductEventComponent implements OnInit, AfterViewInit {
    activeConfig: FormStateConfig = PRODUCT_EVENT_FORM_RULES['DEFAULT'];
    defaultEventDate!: string;
    existingEventDate = '';
    readonly displayEditProductEvent = output<boolean>();
    editProductEvent!: FormGroup;
    eventTypeKeys: (keyof typeof ProductEventType)[] = [];
    eventTypeOptions = signal<SelectOption[]>([]);
    festivals: Festival[] = [];
    festivalSelectOptions = signal<SelectOption[]>([]);
    readonly firstInputField = viewChild<ElementRef<HTMLInputElement>>('firstField');
    initialEventTypeId = signal<number | string | null>(null);
    initialFestivalId = signal<number | string | null>(null);
    initialFromStorageTypeId = signal<number | string | null>(null);
    initialPartnerId = signal<number | string | null>(null);
    initialProductId = signal<number | string | null>(null);
    initialToStorageTypeId = signal<number | string | null>(null);
    maxDate!: string;
    partners: Partner[] = [];
    partnerSelectOptions = signal<SelectOption[]>([]);
    productEvent?: ProductEvent;
    productEventType = ProductEventType;
    productSelectOptions = signal<SelectOption[]>([]);
    products: Material[] = [];
    selectedProductEventTypeOption: string | null = null;
    storageType = StorageType;
    storageTypeKeys: (keyof typeof StorageType)[] = [];
    storageTypeOptions = signal<SelectOption[]>([]);
    storeBaseUrl = inject<string>('STORE_BACKEND_URL' as never);
    updateProductEventURI = '/admin/updateProductEvent';

    private _fb = inject(FormBuilder);

    private _router = inject(Router);

    private _location = inject(Location);

    private _apiRequestService = inject(ApiRequestService);

    private _toastMessageService = inject(ToastmessageService);

    private _translateService = inject(TranslationService);

    private _formHelper = inject(ProductEventFormHelperService);

    private _dateUtil = inject(DateUtilService);

    private _cdr = inject(ChangeDetectorRef);

    private _recalculationService = inject(RecalculationService);

    private _referenceData = inject(ReferenceDataService);

    ngOnInit(): void {
        this.eventTypeKeys = this._getProductEventTypeEnumKeys(this.productEventType);
        this.storageTypeKeys = SELECTABLE_STORAGE_TYPES;
        this.maxDate = this._dateUtil.getMaxDateForDateSelection(true);
        this.eventTypeOptions.set(
            this.eventTypeKeys.map((key) => ({
                id: this.productEventType[key],
                name: this._translateService.translate('admin.product.event.type.' + key)
            }))
        );
        this.storageTypeOptions.set(
            this.storageTypeKeys.map((key) => ({
                id: key,
                name: this._translateService.translate('admin.product.statistics.storage.' + this.storageType[key])
            }))
        );
        this._loadDataAndInitializeForm();
    }

    ngAfterViewInit(): void {
        this.firstInputField()?.nativeElement.focus();
    }

    isFieldVisible(controlName: string): boolean {
        if (!this.activeConfig?.visible || !this.activeConfig.visible.includes(controlName)) {
            return false;
        }
        const control = this.editProductEvent.get(controlName);
        return control ? control.enabled : false;
    }

    isNonSelectableValue(controlName: string): boolean {
        const value = this.editProductEvent.get(controlName)?.value as keyof typeof StorageType | null;
        return !!value && !this.storageTypeKeys.includes(value);
    }

    getStorageTypeTranslationKey(controlName: string): string {
        const value = this.editProductEvent.get(controlName)?.value as keyof typeof StorageType;
        return 'admin.product.statistics.storage.' + this.storageType[value];
    }

    navigateBack(): void {
        this._location.back();
    }

    onUpdateProductEvent(): void {
        const updateProductEventRequest: UpdateProductEventRequest = this.editProductEvent.value;

        const rawProductEventData = this.editProductEvent.getRawValue();
        updateProductEventRequest.productEventType = rawProductEventData.productEventType;
        updateProductEventRequest.fromStorageType = rawProductEventData.fromStorageType;
        updateProductEventRequest.toStorageType = rawProductEventData.toStorageType;

        updateProductEventRequest.id = this.productEvent?.id;

        this._apiRequestService
            .sendRequest<UpdateProductEventRequest, Void>(this.storeBaseUrl, updateProductEventRequest, true, this.updateProductEventURI)
            .pipe(switchMap(() => this._triggerRecalculation()))
            .subscribe(() => {
                this._toastMessageService.displayToastMessage(
                    TOAST_STATE.success,
                    this._translateService.translate(SUCCESSFULLY_MODIFIED),
                    MAT_ICON.success
                );
                this.displayEditProductEvent.emit(false);
                void this._router.navigateByUrl('/productevents');
            });
    }

    onEventTypeSelect(id: number | string | null): void {
        this.editProductEvent.get('productEventType')?.setValue(id);
    }

    onFromStorageTypeSelect(id: number | string | null): void {
        this.editProductEvent.get('fromStorageType')?.setValue(id);
    }

    onToStorageTypeSelect(id: number | string | null): void {
        this.editProductEvent.get('toStorageType')?.setValue(id);
    }

    onProductSelect(id: number | string | null): void {
        this.editProductEvent.get('productId')?.setValue(id);
    }

    onFestivalSelect(id: number | string | null): void {
        this.editProductEvent.get('festivalId')?.setValue(id);
    }

    onPartnerSelect(id: number | string | null): void {
        this.editProductEvent.get('partnerId')?.setValue(id);
    }

    onEventDateSelected(date: Date | null): void {
        const value = date
            ? `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
            : '';
        this.editProductEvent.get('eventDate')?.setValue(value);
    }

    private _showFutureDateError(): void {
        this._toastMessageService.displayToastMessage(
            TOAST_STATE.error,
            this._translateService.translate(FUTURE_DATE_ERROR),
            MAT_ICON.error
        );
    }

    private _getProductEventTypeEnumKeys(enumObject: any): (keyof typeof ProductEventType)[] {
        return Object.keys(enumObject).filter((key) => isNaN(Number(key))) as (keyof typeof ProductEventType)[];
    }

    private _loadDataAndInitializeForm(): void {
        const productEvent = history.state?.productEvent;

        if (!productEvent || productEvent.productEventType === ProductEventType.SOLD_VIA_WEBSHOP) {
            void this._router.navigateByUrl('/productevents');
            return;
        }

        this.productEvent = productEvent;

        forkJoin({
            products: this._referenceData.loadProducts(),
            partners: this._referenceData.loadPartners(),
            festivals: this._referenceData.loadFestivals()
        }).subscribe({
            next: (responses) => {
                this.products = responses.products;
                this.partners = responses.partners;
                this.festivals = responses.festivals;

                this.productSelectOptions.set(this.products.map((p) => ({ id: p.id!, name: p.author + ': ' + p.title })));
                this.festivalSelectOptions.set(this.festivals.map((f) => ({ id: f.id!, name: f.name! })));
                this.partnerSelectOptions.set(this.partners.map((p) => ({ id: p.id!, name: p.name! })));

                this._initializeForm();
                this._cdr.markForCheck();
            },
            error: () => {
                this._toastMessageService.displayToastMessage(
                    TOAST_STATE.error,
                    this._translateService.translate('admin.product.event.error.loadFailed'),
                    MAT_ICON.error
                );
            }
        });
    }

    private _initializeForm(): void {
        const editableEventDate = new Date(this._dateUtil.formatDate(this.productEvent?.eventDate));
        this.defaultEventDate = `${editableEventDate?.getFullYear()}-${(editableEventDate?.getMonth() + 1)
            .toString()
            .padStart(2, '0')}-${editableEventDate?.getDate().toString().padStart(2, '0')}`;
        this.existingEventDate = this.defaultEventDate;

        const futureDateValidator = this._dateUtil.futureDateValidator(() => this._showFutureDateError());

        this.editProductEvent = this._fb.group({
            productEventType: new FormControl(
                {
                    value: this.productEvent?.productEventType,
                    disabled: true
                },
                Validators.required
            ),
            fromStorageType: [this.productEvent?.fromStorageType, [Validators.required]],
            toStorageType: [this.productEvent?.toStorageType, [Validators.required]],
            productId: [this.productEvent?.productId, [Validators.required]],
            partnerId: [this.productEvent?.partnerId, [Validators.required]],
            festivalId: [this.productEvent?.festivalId, [Validators.required]],
            orderDetailsReference: [this.productEvent?.referenceNumber],
            productReference: [this.productEvent?.referenceNumber],
            quantity: [this.productEvent?.quantity, [Validators.required]],
            unitPrice: [this.productEvent?.unitPrice, [Validators.required]],
            eventDate: [this.defaultEventDate, [Validators.required, futureDateValidator]],
            comment: [this.productEvent?.comment]
        });

        const selectedType = Object.entries(this.productEventType).find(([, value]) => value === this.productEvent?.productEventType);
        this.selectedProductEventTypeOption = selectedType![1];
        this._toggleConditionalField(this.selectedProductEventTypeOption);

        this.editProductEvent.patchValue({
            fromStorageType: this.productEvent?.fromStorageType ?? null,
            toStorageType: this.productEvent?.toStorageType ?? null
        });

        this.initialEventTypeId.set(this.productEvent?.productEventType ?? null);
        this.initialFromStorageTypeId.set(this.productEvent?.fromStorageType ?? null);
        this.initialToStorageTypeId.set(this.productEvent?.toStorageType ?? null);
        this.initialProductId.set(this.productEvent?.productId ?? null);
        this.initialFestivalId.set(this.productEvent?.festivalId ?? null);
        this.initialPartnerId.set(this.productEvent?.partnerId ?? null);
    }

    private _toggleConditionalField(value: string | null): void {
        this.activeConfig =
            value && PRODUCT_EVENT_FORM_RULES[value] ? PRODUCT_EVENT_FORM_RULES[value] : PRODUCT_EVENT_FORM_RULES['DEFAULT'];

        this._formHelper.applyFormRules(this.editProductEvent, value);
    }

    private _triggerRecalculation(): Observable<Void> {
        const productId = this.productEvent?.productId;
        const newEventDate = this.editProductEvent.get('eventDate')?.value;
        if (!productId || !newEventDate) {
            return of(undefined as unknown as Void);
        }
        const request = this._recalculationService.buildEditRecalculationRequest(
            productId, this.existingEventDate, String(newEventDate)
        );
        if (!request) {
            return of(undefined as unknown as Void);
        }
        return this._recalculationService.sendRecalculationRequest(request);
    }
}
